// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Modal } from '@loa/ui-components';
import { policyApi } from '../../api/policyApi';

type DeletePolicyModalProps = Readonly<{
  open: boolean;
  profileId: string;
  policyId: string;
  onClose: () => void;
  onSuccess: () => void;
}>;

export function DeletePolicyModal({
  open,
  profileId,
  policyId,
  onClose,
  onSuccess,
}: DeletePolicyModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);
      await policyApi.deletePolicy(profileId, policyId, 'Deleted by advisor');
      onSuccess();
    } catch (err) {
      const apiError = err as { response?: { status?: number; data?: { message?: string } } };
      if (apiError.response?.status === 409) {
        setError(
          apiError.response.data?.message ||
            'This policy cannot be deleted because it has active letters of authority or other dependencies.'
        );
      } else {
        setError(apiError.response?.data?.message || 'Failed to delete policy. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="small" variant="warning">
      <Modal.Header icon="trash" title="Delete Policy" onClose={handleClose} />
      <Modal.Content>
        <div className="text-[#000] font-sans text-body-01 font-normal leading-normal">
          Are you sure you want to delete this policy?
          <br /><br />
          This will delete all the metadata, documents and LoAs related to this policy.
          {error && (
            <div className="mt-md p-[12px] bg-[#FFF2F3] border border-error-text rounded-lg text-error-text text-body-03">
              {error}
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: loading, colour: 'red' }}
        primaryAction={{ label: 'Delete Policy', onClick: () => { void handleDelete(); }, loading, disabled: loading, colour: 'red' }}
      />
    </Modal>
  );
}
